import React, {useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronUp, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {faCirclePlus} from "@fortawesome/free-solid-svg-icons/faCirclePlus";
import {faUserTie} from "@fortawesome/free-solid-svg-icons/faUserTie";
import {resourcePlanItemDefault} from "../../../../defaults/Items";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import ResourcePlanItem from "./ResourcePlanItem";

const fixedPeriodDays = {DAILY: 1, WEEKEND: 2, WEEKLY: 7, BIWEEKLY: 14, MONTHLY: 31};
const maxDaysForPlan = (plan) => plan.periodType === "CUSTOM"
  ? Math.max(1, Number(plan.periodDays || 1))
  : (fixedPeriodDays[plan.periodType] || 1);

const ResourcePlanItemsCard = ({state, positionsList, departmentsList}) => {
  const currentItem = state.currentItem;
  const resourcePlanItems = currentItem?.resourcePlanItems || [];
  const setCurrentItem = state.setCurrentItem;
  const setItemChanged = state.setItemChanged;
  const [resourcePlanItemNote] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [copyFromDay, setCopyFromDay] = useState(1);
  const [copyToDay, setCopyToDay] = useState(2);
  const maxDays = maxDaysForPlan(currentItem);

  const markChanged = () => {
    setItemChanged((prev) => !prev);
    state.setCurrentFormState((prev) => ({...prev, formSaveButtonDisabled: false}));
  };

  const onInputChange = (e, resourcePlanItem, field) => {
    const rawValue = e.target.value;
    const value = field === "requiredPeopleCount" || field === "priority"
      ? (rawValue === "" ? "" : Number(rawValue))
      : rawValue;
    setCurrentItem((prev) => ({
      ...prev,
      resourcePlanItems: (prev.resourcePlanItems || []).map((item) =>
        item.id === resourcePlanItem.id ? {...item, [field]: value} : item
      )
    }));
    markChanged();
  };

  const addResourcePlanItem = () => {
    setCurrentItem((prev) => {
      const items = prev.resourcePlanItems || [];
      const existingIds = new Set(items.map((item) => item.id));
      let newId = -1;
      while (existingIds.has(newId)) newId--;

      const previous = items[items.length - 1];
      const newItem = {
        ...resourcePlanItemDefault,
        id: newId,
        dayIndex: previous?.dayIndex || 1,
        department: previous?.department?.id ? previous.department : {id: 0},
        positions: [],
        position: null,
        allOtherPositions: false,
        requiredPeopleCount: 1,
        priority: previous?.priority ?? 5,
        workStart: previous?.workStart || prev.workStart || "",
        workEnd: previous?.workEnd || prev.workEnd || ""
      };
      return {...prev, resourcePlanItems: [...items, newItem]};
    });
    markChanged();
  };

  const removeResourcePlanItem = (itemId) => {
    setCurrentItem((prev) => ({
      ...prev,
      resourcePlanItems: (prev.resourcePlanItems || []).filter((item) => item.id !== itemId)
    }));
    markChanged();
  };

  const copySourceDayToTargets = (sourceDay, targetDays) => {
    const source = resourcePlanItems.filter((item) => Number(item.dayIndex || 1) === sourceDay);
    if (source.length === 0) {
      toastError(`Dzień ${sourceDay} nie ma żadnych pozycji do skopiowania.`);
      return false;
    }

    const targetSet = new Set(targetDays);
    const remainingItems = resourcePlanItems.filter((item) => !targetSet.has(Number(item.dayIndex || 1)));
    const existingIds = new Set(remainingItems.map((item) => item.id));
    let nextId = -1;
    const clones = [];

    targetDays.forEach((targetDay) => {
      source.forEach((item) => {
        while (existingIds.has(nextId)) nextId--;
        existingIds.add(nextId);
        clones.push({
          ...item,
          id: nextId--,
          dayIndex: targetDay,
          positions: Array.isArray(item.positions) ? item.positions.map((position) => ({...position})) : []
        });
      });
    });

    const nextItems = [...remainingItems, ...clones].sort((a, b) =>
      Number(a.dayIndex || 1) - Number(b.dayIndex || 1)
    );
    setCurrentItem((prev) => ({...prev, resourcePlanItems: nextItems}));
    markChanged();
    return true;
  };

  const copyDay = () => {
    const targetDay = Math.min(maxDays, Math.max(1, Number(copyToDay)));
    const sourceDay = Math.min(maxDays, Math.max(1, Number(copyFromDay)));
    if (sourceDay === targetDay) {
      toastError("Dzień źródłowy i docelowy są takie same.");
      return;
    }
    if (copySourceDayToTargets(sourceDay, [targetDay])) {
      toastSuccess(`Skopiowano dzień ${sourceDay} na dzień ${targetDay}.`);
    }
  };

  const copyDayToWholePlan = () => {
    const sourceDay = Math.min(maxDays, Math.max(1, Number(copyFromDay)));
    const targetDays = Array.from({length: maxDays}, (_, index) => index + 1)
      .filter((day) => day !== sourceDay);
    if (copySourceDayToTargets(sourceDay, targetDays)) {
      toastSuccess(`Skopiowano dzień ${sourceDay} na wszystkie pozostałe dni planu.`);
    }
  };

  return (
    <>
      <div className="resource-plan-items-card">
        <div
          className="resource-plan-items-card-header"
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faUserTie} className="me-2 header-icon"/>
            <span>Zapotrzebowanie {!isAccordionOpen ? `(${resourcePlanItems.length})` : ""}</span>
          </div>
          <FontAwesomeIcon icon={isAccordionOpen ? faChevronUp : faChevronDown}/>
        </div>

        {isAccordionOpen && (
          <div className="resource-plan-items-body" id="resourcePlanItems">
            <div className="resource-plan-toolbar">
              <button type="button" className="btn btn-primary btn-sm" onClick={addResourcePlanItem}>
                <FontAwesomeIcon icon={faCirclePlus}/> Dodaj pozycję
              </button>

              {maxDays > 1 && (
                <div className="resource-plan-toolbar-group">
                  <span className="small">Kopiuj dzień</span>
                  <select
                    className="form-select form-select-sm"
                    style={{width: "92px"}}
                    value={Math.min(copyFromDay, maxDays)}
                    onChange={(e) => setCopyFromDay(Number(e.target.value))}
                  >
                    {Array.from({length: maxDays}, (_, i) => i + 1).map((day) => <option key={day} value={day}>{day}</option>)}
                  </select>
                  <span className="small">na</span>
                  <select
                    className="form-select form-select-sm"
                    style={{width: "92px"}}
                    value={Math.min(copyToDay, maxDays)}
                    onChange={(e) => setCopyToDay(Number(e.target.value))}
                  >
                    {Array.from({length: maxDays}, (_, i) => i + 1).map((day) => <option key={day} value={day}>{day}</option>)}
                  </select>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copyDay}>Powiel</button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={copyDayToWholePlan}
                    title="Zastępuje zawartość wszystkich pozostałych dni pozycjami z wybranego dnia"
                  >
                    Powiel na cały plan
                  </button>
                </div>
              )}

              <div className="resource-plan-toolbar-note">
                Jedna pozycja opisuje zapotrzebowanie dla konkretnego dnia i godzin. Brak wybranego stanowiska oznacza pulę wszystkich aktywnych stanowisk wskazanego działu.
              </div>
            </div>

            {resourcePlanItems.length > 0 ? (
              <div className="resource-plan-items-scroll">
                <div className="resource-plan-items-table">
                  <div className="resource-plan-items-grid resource-plan-items-header">
                    <div>Dzień</div>
                    <div>Dział</div>
                    <div>Stanowiska / pula</div>
                    <div>Liczba osób</div>
                    <div>Priorytet</div>
                    <div>Początek</div>
                    <div>Koniec</div>
                    <div></div>
                  </div>

                  {resourcePlanItems.map((resourcePlanItem, index) => (
                    <ResourcePlanItem
                      key={resourcePlanItem.id}
                      resourcePlanItem={resourcePlanItem}
                      index={index}
                      state={state}
                      onInputChange={onInputChange}
                      positionsList={positionsList}
                      departmentsList={departmentsList}
                      onRemove={() => removeResourcePlanItem(resourcePlanItem.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-muted small py-3">Brak pozycji zapotrzebowania. Dodaj pierwszą pozycję albo skopiuj wcześniej przygotowany dzień.</div>
            )}
          </div>
        )}
      </div>

      <p id="certificationNote" className={resourcePlanItemNote ? "err_info" : "offscreen"}>
        <FontAwesomeIcon icon={faInfoCircle}/>&nbsp; Uzupełnij dane zapotrzebowania
      </p>
    </>
  );
};

export default ResourcePlanItemsCard;
